import React, { useCallback, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { samplePages } from '../data/coachSystem';
export function InsideSection() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.firstElementChild as HTMLElement | null;
    if (!slide) return;
    const step = slide.offsetWidth + 16;
    setActiveIndex(Math.min(samplePages.length - 1, Math.max(0, Math.round(track.scrollLeft / step))));
  }, []);
  const scrollTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.children[index] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft,
      behavior: 'smooth'
    });
  };
  return <section className="border-t border-navy-line bg-ivory py-11 sm:py-14" aria-labelledby="inside-title" data-id="element-59" data-mp="145w6zdolntbc">
      <div className="mx-auto w-full max-w-[42rem] px-4 text-center sm:px-6 lg:max-w-5xl" data-id="element-60" data-mp="2vuq2s7bkcykc">
        <p className="eyebrow text-[0.66rem] font-semibold text-teal sm:text-[0.72rem]" data-id="element-61" data-mp="39lv5xje548zf">
          A look inside
        </p>
        <h2 id="inside-title" className="mt-3 font-display text-[1.75rem] leading-[1.15] tracking-tight text-navy sm:text-[2.25rem]" data-id="element-62" data-mp="34mi9c6xgvnjn">
          See what’s inside the system
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-[0.98rem] leading-relaxed text-navy-muted" data-id="element-63" data-mp="2cwni24q1tnsc">
          Real drills. Real court diagrams. Real coaching systems. Complete
          resources already built for you.
        </p>
      </div>

      <div className="relative mt-6" data-id="element-64" data-mp="2lydcljf892uc">
        <ul ref={trackRef} onScroll={handleScroll} className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-1 sm:px-6 lg:px-[max(1.5rem,calc((100%-64rem)/2))]" aria-label="Sample pages from Volleyball Coach System" data-id="element-65" data-mp="64mqf66vxl7v">
          {samplePages.map((page, index) => <li key={page.src} className="w-[78vw] max-w-[22rem] shrink-0 snap-center sm:w-[20rem] lg:w-[22rem]" aria-current={index === activeIndex ? 'true' : undefined} data-id="element-66" data-mp="d6jd3cenz5cr" data-mpi={index}>
              <figure className="flex h-full flex-col overflow-hidden rounded-2xl border border-navy-line bg-ivory shadow-card" data-id="element-67" data-mp="3lo5vyucksew0">
                <div className="aspect-[3/4] w-full bg-white" data-id="element-68" data-mp="3qmh6766s8rer">
                  <img src={page.src} alt={page.alt} loading="lazy" className="h-full w-full object-contain" data-id="element-69" data-mp="3vmc1dlxlc35m" />
                </div>
                <figcaption className="border-t border-navy-line px-4 py-3 text-center" data-id="element-70" data-mp="32yg3wf07677s">
                  <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-sand" data-id="element-71" data-mp="37w7u94l71l9v">
                    {page.category}
                  </p>
                  <p className="mt-1 text-[0.85rem] font-semibold leading-snug text-navy" data-id="element-72" data-mp="30b5pwtbfsqsn">
                    {page.title}
                  </p>
                </figcaption>
              </figure>
            </li>)}
        </ul>
      </div>

      <div className="mx-auto mt-4 w-full max-w-[42rem] px-4 sm:px-6 lg:max-w-5xl" data-id="element-73" data-mp="2lgr3eisnyz98">
        <div className="flex items-center justify-center gap-4" data-id="element-74" data-mp="2he4a0dgch69x">
          <button type="button" onClick={() => scrollTo(Math.max(0, activeIndex - 1))} disabled={activeIndex === 0} aria-label="Previous page" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-navy-line bg-white text-navy transition-colors duration-150 ease-out hover:border-teal-mid hover:text-teal disabled:opacity-35 disabled:hover:border-navy-line disabled:hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal" data-id="element-75" data-mp="2hgh0jf064oyl">
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-76" data-mp="2ig7o1z0cb6jp" />
          </button>

          <ul className="flex items-center gap-1.5" aria-hidden="true" data-id="element-77" data-mp="2ne0sonlzvshv">
            {samplePages.map((page, index) => <li key={page.src} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${index === activeIndex ? 'w-5 bg-teal' : 'w-1.5 bg-teal-mid/70'}`} data-id="element-78" data-mp="29lb88nm46vmv" data-mpi={index} />)}
          </ul>

          <button type="button" onClick={() => scrollTo(Math.min(samplePages.length - 1, activeIndex + 1))} disabled={activeIndex === samplePages.length - 1} aria-label="Next page" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-navy-line bg-white text-navy transition-colors duration-150 ease-out hover:border-teal-mid hover:text-teal disabled:opacity-35 disabled:hover:border-navy-line disabled:hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal" data-id="element-79" data-mp="24lolnnkec31e">
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-80" data-mp="1n744jvywfxe3" />
          </button>
        </div>

        <p className="mx-auto mt-6 max-w-md border-t-2 border-sand pt-4 text-center text-[0.95rem] leading-relaxed text-navy-muted" data-id="element-81" data-mp="1ieuac3qr4b60">
          Everything is designed to help you{' '}
          <span className="font-semibold text-navy" data-id="element-82" data-mp="1s4dst8ajy30c">
            OPEN → CHOOSE → COACH.
          </span>
        </p>
      </div>
    </section>;
}